import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Ticket, Plus, Trash2, CalendarRange, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

function isExpired(validUntil) {
  if (!validUntil) return false;
  const today = new Date().toISOString().slice(0, 10);
  return today > validUntil;
}

export default function CouponsManagement() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | coupon object
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coupons");
      setCoupons(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      await api.delete(`/coupons/${deleting.id}`);
      toast.success("クーポンを削除しました");
      setDeleting(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="coupons-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">クーポン管理</h1>
          <p className="text-sm text-slate-500 mt-1">資料請求フォームで適用できるクーポンコードの作成・編集。</p>
        </div>
        <Button onClick={() => setEditing("new")} className="bg-blue-600 hover:bg-blue-700" data-testid="new-coupon">
          <Plus className="w-4 h-4 mr-1" />新しいクーポン
        </Button>
      </div>

      <Card className="bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <p className="py-12 text-center text-sm text-slate-400">読み込み中…</p>
        ) : coupons.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">クーポンがありません。「新しいクーポン」から作成してください。</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {coupons.map((c) => {
              const expired = isExpired(c.valid_until);
              const active = c.is_active && !expired;
              return (
                <li key={c.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50" data-testid={`coupon-row-${c.id}`}>
                  <div className={`mt-0.5 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    active ? "bg-gradient-to-br from-amber-100 to-pink-100 text-amber-700" : "bg-slate-100 text-slate-400"
                  }`}>
                    <Ticket className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setEditing(c)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-mono font-bold text-sm text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{c.code}</code>
                      {active ? (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" />有効</span>
                      ) : (
                        <span className="text-[10px] bg-slate-200 text-slate-600 rounded-full px-2 py-0.5 inline-flex items-center gap-0.5"><XCircle className="w-3 h-3" />{expired ? "期限切れ" : "無効"}</span>
                      )}
                      <span className="text-[11px] text-slate-500 inline-flex items-center gap-0.5"><CalendarRange className="w-3 h-3" />{c.valid_until} まで</span>
                      <span className="text-[11px] text-slate-500 ml-auto">使用 <b className="text-slate-900">{c.usage_count || 0}</b>件</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{c.label}</p>
                    {c.description && <p className="mt-0.5 text-xs text-slate-500">{c.description}</p>}
                  </div>
                  <Button variant="ghost" size="sm" className="text-rose-500 hover:bg-rose-50" onClick={() => setDeleting(c)} data-testid={`delete-coupon-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {editing && (
        <CouponEditDialog coupon={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={load} />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>クーポンを削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              <code className="font-mono">{deleting?.code}</code> ({deleting?.label}) を削除します。<br />
              すでにこのクーポンを使用したリードの記録は残ります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete">削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CouponEditDialog({ coupon, onClose, onSaved }) {
  const isNew = !coupon;
  const [code, setCode] = useState(coupon?.code || "");
  const [label, setLabel] = useState(coupon?.label || "");
  const [validUntil, setValidUntil] = useState(coupon?.valid_until || "");
  const [description, setDescription] = useState(coupon?.description || "");
  const [isActive, setIsActive] = useState(coupon?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!code || !label || !validUntil) {
      toast.error("コード・内容・有効期限は必須です");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/coupons", {
          code: code.trim().toLowerCase(),
          label: label.trim(),
          valid_until: validUntil,
          description: description.trim() || null,
          is_active: isActive,
        });
        toast.success("クーポンを作成しました");
      } else {
        await api.put(`/coupons/${coupon.id}`, {
          code: code.trim().toLowerCase(),
          label: label.trim(),
          valid_until: validUntil,
          description: description.trim() || null,
          is_active: isActive,
        });
        toast.success("クーポンを更新しました");
      }
      await onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="coupon-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-blue-600" />{isNew ? "新しいクーポン" : "クーポンを編集"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold text-slate-700">クーポンコード <span className="text-rose-500">*</span></Label>
            <Input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 font-mono" placeholder="例: yasai808" data-testid="coupon-code" />
            <p className="text-[10px] text-slate-400 mt-1">小文字に正規化されます。3〜40文字。</p>
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">内容 (顧客に表示される文言) <span className="text-rose-500">*</span></Label>
            <Input type="text" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" placeholder="例: 2ヶ月無料 + 3ヶ月目 50%オフ" data-testid="coupon-label" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">有効期限 <span className="text-rose-500">*</span></Label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1" data-testid="coupon-valid-until" />
            <p className="text-[10px] text-slate-400 mt-1">この日まで（含む）に資料請求した人が適用可能。</p>
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">メモ (任意・社内用)</Label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2 resize-none" placeholder="キャンペーン名・配布チャネルなど" data-testid="coupon-description" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="coupon-active-label">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4" data-testid="coupon-active" />
            有効にする（チェックを外すと、コードを入力されても適用されません）
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>キャンセル</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="coupon-save">
            {saving ? "保存中…" : isNew ? "作成する" : "更新する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
